import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle, ArrowUpRight, Ban, Calendar, CheckCircle2, ChevronDown,
    ChevronRight, Clock, Eye, FlaskConical, History, Info, Key, Loader2,
    Mail, RefreshCw, Rocket, RotateCcw, Send, Shield, ShieldAlert, Sparkles,
    Timer, Users, X, Zap,
} from "lucide-react";
import { toast } from "sonner";

import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { apiFetch, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";
import {
    KpiCard, EngineChip, StatusBadge, FlagBanner, defaultScheduleISO,
    ConfirmModal, SuggestionRow, timeUntil, LogRow, StepDot, RolloutBanner,
    STEP_INFO,
} from "@/components/email-send/EmailSendPrimitives";



// ---- main dashboard -------------------------------------------------------

export default function EmailSendDashboard() {
    const { user: me } = useAuth();
    const isAdmin = isAdminRole(me?.role);

    const [statusData, setStatusData] = useState(null);
    const [rolloutData, setRolloutData] = useState(null);
    const [totals, setTotals] = useState({});
    const [suggestions, setSuggestions] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const [confirmState, setConfirmState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [flagBusy, setFlagBusy] = useState(false);

    const [scheduleAt, setScheduleAt] = useState(defaultScheduleISO(15));
    const [retractReason, setRetractReason] = useState("");
    const [previewData, setPreviewData] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [rolloutReason, setRolloutReason] = useState("");
    const [rolloutConfirmType, setRolloutConfirmType] = useState("");

    const loadFlagsAndTotals = useCallback(async () => {
        try {
            const settings = await apiFetch("/admin/system-settings");
            const toggles = settings.toggles || {};
            const enabled = !!toggles.EMAIL_ENGINE_SEND_ENABLED;
            if (!enabled) {
                setStatusData(null);
                setRolloutData(null);
                setTotals({});
                setSuggestions([]); setLogs([]);
                return;
            }
            const s = await apiFetch("/email-send/status");
            setStatusData(s);
            const [t, appl, l, rollout] = await Promise.all([
                apiFetch("/email-send/totals"),
                apiFetch("/email-send/applicable?limit=100"),
                apiFetch("/email-send/logs?limit=50"),
                api.emailSendRolloutStatus().catch(() => null),
            ]);
            setTotals(t || {});
            setSuggestions(appl?.items || []);
            setLogs(l?.items || []);
            setRolloutData(rollout);
        } catch (e) {
            if (e.status === 503) {
                setStatusData(null); setRolloutData(null); setTotals({});
                setSuggestions([]); setLogs([]);
            } else {
                toast.error(e.message || "Failed to load Email Send state");
            }
        }
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await loadFlagsAndTotals();
            setLoading(false);
        })();
    }, [loadFlagsAndTotals]);

    // ---- flag toggles ----------------------------------------------------
    const askToggleMaster = () => {
        const next = !statusData;   // if null → we're currently off → next=true
        setConfirmState({
            kind: "toggle_master", next,
            title: next ? "Enable Email Send Layer?" : "Disable Email Send Layer?",
            subtitle: next
                ? "You are about to enable the SECOND phase with external side-effects. Sandbox mode is ON by default (log-only), so no real emails will be delivered until you explicitly flip sandbox off."
                : "All dispatch endpoints will return 503 immediately. Existing scheduled entries remain in place (they will not tick to `due` until you re-enable).",
            tone: next ? "warning" : "alert",
            confirmLabel: next ? "Enable now" : "Disable",
            children: (
                <div className="text-xs text-text-secondary space-y-2">
                    <p>Key: <span className="font-mono text-text-main">EMAIL_ENGINE_SEND_ENABLED</span></p>
                    <p>New value: <span className={`font-semibold ${next ? "text-neutral-200" : "text-[var(--color-alert)]"}`}>{String(next)}</span></p>
                    <p className="italic">Sandbox flag remains unchanged.</p>
                </div>
            ),
        });
    };

    const askToggleSandbox = () => {
        const next = !statusData?.sandbox_mode;
        setConfirmState({
            kind: "toggle_sandbox", next,
            title: next ? "Return to sandbox mode?" : "Enable PRODUCTION dispatch?",
            subtitle: next
                ? "All dispatches will log-only. No external Resend calls. Safe default."
                : "REAL EMAILS WILL BE DELIVERED. Rate limit still applies (5/day). Make sure the Resend API key is configured and OUTREACH_SANDBOX_MODE is off.",
            tone: next ? "success" : "alert",
            confirmLabel: next ? "Back to sandbox" : "Go live",
            children: (
                <div className="text-xs text-text-secondary space-y-2">
                    <p>Key: <span className="font-mono text-text-main">EMAIL_ENGINE_SEND_SANDBOX</span></p>
                    <p>New value: <span className={`font-semibold ${next ? "text-white" : "text-[var(--color-alert)]"}`}>{String(next)}</span></p>
                    {!next && (
                        <p className="text-[var(--color-alert)] font-semibold italic">
                            You are about to enable REAL delivery. Confirm only if the Resend key
                            is set and the founder approved production sends.
                        </p>
                    )}
                </div>
            ),
        });
    };

    const confirmToggleFlag = async (key, next) => {
        setFlagBusy(true);
        try {
            await apiFetch(`/admin/system-settings/${key}`, {
                method: "PATCH",
                body: JSON.stringify({
                    value: next,
                    reason: `Toggle from /admin/email-send`,
                }),
            });
            toast.success(`${key} = ${next}`);
            setConfirmState(null);
            await loadFlagsAndTotals();
        } catch (e) {
            toast.error(e.message || "Toggle failed");
        } finally {
            setFlagBusy(false);
        }
    };

    // ---- dispatch flow ----------------------------------------------------
    const askDispatch = (suggestion) => {
        setConfirmState({
            kind: "dispatch", suggestion,
            title: "Dispatch this email suggestion?",
            subtitle: statusData?.sandbox_mode
                ? "Sandbox mode is ON — no external delivery. Audit + unsubscribe tokens still generated."
                : "PRODUCTION MODE — real emails will be delivered.",
            tone: statusData?.sandbox_mode ? "primary" : "warning",
            confirmLabel: "Confirm dispatch",
            children: (
                <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                        <EngineChip engine={suggestion.engine_source} />
                        {suggestion.target_user_count != null && (
                            <span className="text-[11px] text-text-secondary flex items-center gap-1">
                                <Users className="h-3 w-3" /> {suggestion.target_user_count} recipient(s)
                            </span>
                        )}
                    </div>
                    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3">
                        <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">Subject</div>
                        <div className="text-sm text-text-main">{suggestion.subject}</div>
                    </div>
                    <div className="rounded-lg border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-3 text-[11px] text-text-secondary">
                        <span className="text-[var(--color-primary)] font-semibold">Compliance guaranteed.</span>{" "}
                        Every recipient gets an unsubscribe token. Users with <code>email_opt_out=true</code> are automatically skipped.
                    </div>
                </div>
            ),
        });
    };

    const confirmDispatch = async (suggestion) => {
        setBusy(true);
        try {
            const r = await apiFetch(
                `/email-send/dispatch/${encodeURIComponent(suggestion.id)}`,
                { method: "POST", body: JSON.stringify({}) },
            );
            const label = r.sandbox_mode ? "sandbox_dropped" : (r.status || "sent");
            toast.success(`Dispatched · ${r.recipients_count} recipient(s) · ${label}`);
            setConfirmState(null);
            await loadFlagsAndTotals();
        } catch (e) {
            if (e.status === 429) toast.error("Daily quota reached (5/day).");
            else if (e.status === 422) toast.error(e.data?.detail?.message || e.message || "Not dispatchable");
            else if (e.status === 403) toast.error(e.data?.detail?.message || "Not acknowledged");
            else if (e.status === 503) { toast.error("Email Send Layer is DISABLED."); await loadFlagsAndTotals(); }
            else toast.error(e.message || "Dispatch failed");
        } finally {
            setBusy(false);
        }
    };

    // ---- schedule flow ----------------------------------------------------
    const askSchedule = (suggestion) => {
        setScheduleAt(defaultScheduleISO(15));
        setConfirmState({
            kind: "schedule", suggestion,
            title: "Schedule email dispatch",
            subtitle: "Recipients are frozen at schedule-time. When scheduled_at arrives, the entry transitions to `due` — you must manually click Send now.",
            tone: "primary",
            confirmLabel: "Schedule",
            children: null, // rendered below because we need state
        });
    };

    const confirmSchedule = async (suggestion) => {
        setBusy(true);
        try {
            // datetime-local produces "YYYY-MM-DDTHH:mm" (local timezone).
            // Convert to ISO with local offset via Date.
            const iso = new Date(scheduleAt).toISOString();
            const r = await apiFetch(
                `/email-send/schedule/${encodeURIComponent(suggestion.id)}`,
                { method: "POST", body: JSON.stringify({ scheduled_at: iso }) },
            );
            toast.success(`Scheduled · ${r.recipients_count} recipient(s) · ${iso.slice(0, 16).replace("T", " ")}`);
            setConfirmState(null);
            await loadFlagsAndTotals();
        } catch (e) {
            if (e.status === 422) toast.error(e.data?.detail?.message || "Invalid schedule");
            else if (e.status === 429) toast.error("Daily quota reached.");
            else toast.error(e.message || "Schedule failed");
        } finally {
            setBusy(false);
        }
    };

    // ---- log actions ------------------------------------------------------
    const askCancel = (log) => {
        setConfirmState({
            kind: "cancel", log,
            title: "Cancel scheduled dispatch?",
            subtitle: "The entry will be moved to `canceled`. The underlying suggestion returns to `acknowledged` so you can re-schedule.",
            tone: "alert", confirmLabel: "Cancel dispatch",
            children: (
                <div className="text-xs text-text-secondary">
                    <p>Log ID: <span className="font-mono text-text-main">{log.id}</span></p>
                    <p>Recipients frozen: <span className="text-text-main">{log.recipients_count}</span></p>
                    <p>Scheduled at: <span className="font-mono text-text-main">{(log.scheduled_at || "").slice(0, 19).replace("T", " ")} UTC</span></p>
                </div>
            ),
        });
    };

    const confirmCancel = async (log) => {
        setBusy(true);
        try {
            await apiFetch(
                `/email-send/cancel/${encodeURIComponent(log.id)}`,
                { method: "POST", body: JSON.stringify({}) },
            );
            toast.success("Scheduled dispatch canceled");
            setConfirmState(null);
            await loadFlagsAndTotals();
        } catch (e) {
            if (e.status === 409) toast.error(e.data?.detail?.message || "Cannot cancel — already sent");
            else toast.error(e.message || "Cancel failed");
        } finally {
            setBusy(false);
        }
    };

    const askDispatchDue = (log) => {
        setConfirmState({
            kind: "dispatch_due", log,
            title: "Send this due dispatch now?",
            subtitle: "The frozen recipients snapshot will be dispatched immediately.",
            tone: "warning", confirmLabel: "Send now",
            children: (
                <div className="text-xs text-text-secondary">
                    <p>Log ID: <span className="font-mono text-text-main">{log.id}</span></p>
                    <p>Recipients: <span className="text-text-main">{log.recipients_count}</span></p>
                    <p>Sandbox: <span className="text-text-main">{statusData?.sandbox_mode ? "ON (log-only)" : "OFF (real delivery)"}</span></p>
                </div>
            ),
        });
    };

    const confirmDispatchDue = async (log) => {
        setBusy(true);
        try {
            await apiFetch(
                `/email-send/dispatch-due/${encodeURIComponent(log.id)}`,
                { method: "POST", body: JSON.stringify({}) },
            );
            toast.success("Dispatched");
            setConfirmState(null);
            await loadFlagsAndTotals();
        } catch (e) {
            toast.error(e.message || "Send failed");
        } finally {
            setBusy(false);
        }
    };

    const askRetract = (log) => {
        setRetractReason("");
        setConfirmState({
            kind: "retract", log,
            title: "Retract this send?",
            subtitle: "This is an AUDIT LABEL ONLY. The email has already left (or been mocked). Retract does NOT recall the message — it records regret for compliance.",
            tone: "alert", confirmLabel: "Retract (audit-only)",
            children: null,
        });
    };

    const confirmRetract = async (log) => {
        if (!retractReason.trim()) {
            toast.error("Reason is required for retract");
            return;
        }
        setBusy(true);
        try {
            await apiFetch(
                `/email-send/retract/${encodeURIComponent(log.id)}`,
                { method: "POST", body: JSON.stringify({ reason: retractReason.trim() }) },
            );
            toast.success("Retracted (audit-only)");
            setConfirmState(null);
            await loadFlagsAndTotals();
        } catch (e) {
            toast.error(e.message || "Retract failed");
        } finally {
            setBusy(false);
        }
    };

    // ---- preview ----------------------------------------------------------
    const doPreview = async (suggestion) => {
        setPreviewBusy(true);
        setPreviewData({ suggestion, loading: true });
        try {
            const r = await apiFetch(
                `/email-send/preview/${encodeURIComponent(suggestion.id)}`,
                { method: "POST", body: JSON.stringify({ user_id: me?.id }) },
            );
            setPreviewData({ suggestion, ...r, loading: false });
        } catch (e) {
            toast.error(e.message || "Preview failed");
            setPreviewData(null);
        } finally {
            setPreviewBusy(false);
        }
    };

    // ---- Phase 16a Rollout gate --------------------------------------------
    const askAdvanceRollout = () => {
        const nextStep = Math.min(4, (rolloutData?.current_step || 1) + 1);
        setRolloutReason("");
        setRolloutConfirmType("");
        setConfirmState({
            kind: "rollout_advance",
            fromStep: rolloutData?.current_step,
            toStep: nextStep,
            title: `Advance rollout: Step ${rolloutData?.current_step} → Step ${nextStep}`,
            subtitle: `Impact: ${(STEP_INFO[nextStep] || {}).desc || "Widens dispatch scope."}`,
            tone: nextStep === 4 ? "alert" : "primary",
            confirmLabel: "Advance",
            children: null,
        });
    };

    const askRollbackRollout = () => {
        const prevStep = Math.max(1, (rolloutData?.current_step || 1) - 1);
        setRolloutReason("");
        setRolloutConfirmType("");
        setConfirmState({
            kind: "rollout_rollback",
            fromStep: rolloutData?.current_step,
            toStep: prevStep,
            title: `Rollback rollout: Step ${rolloutData?.current_step} → Step ${prevStep}`,
            subtitle: `Narrower dispatch scope. Safety button — audits like an advance.`,
            tone: "warning",
            confirmLabel: "Rollback",
            children: null,
        });
    };

    const confirmRollout = async () => {
        if (!confirmState || !["rollout_advance", "rollout_rollback"].includes(confirmState.kind)) return;
        const wantsType = confirmState.kind === "rollout_advance" ? "ADVANCE" : "ROLLBACK";
        if (rolloutConfirmType.trim().toUpperCase() !== wantsType) {
            toast.error(`Type ${wantsType} to confirm`);
            return;
        }
        if (!rolloutReason.trim()) {
            toast.error("Reason is required (audit)");
            return;
        }
        setFlagBusy(true);
        try {
            await api.emailSendRolloutAdvance(confirmState.toStep, rolloutReason.trim());
            toast.success(`Rollout: Step ${confirmState.fromStep} → Step ${confirmState.toStep}`);
            setConfirmState(null);
            await loadFlagsAndTotals();
        } catch (e) {
            if (e.status === 422) toast.error(e.data?.detail?.message || "Invalid transition");
            else toast.error(e.message || "Rollout change failed");
        } finally {
            setFlagBusy(false);
        }
    };

    // ---- render -----------------------------------------------------------

    const scheduledOnly = useMemo(
        () => logs.filter((l) => l.status === "scheduled" || l.status === "due"),
        [logs],
    );
    const historyOnly = useMemo(
        () => logs.filter((l) => l.status !== "scheduled" && l.status !== "due"),
        [logs],
    );

    if (!isAdmin) {
        return (
            <div
                data-testid="email-send-forbidden"
                className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center"
            >
                <h1 className="text-2xl">Admin only</h1>
            </div>
        );
    }

    const confirmHandler = () => {
        if (!confirmState) return;
        if (confirmState.kind === "toggle_master")
            return confirmToggleFlag("EMAIL_ENGINE_SEND_ENABLED", confirmState.next);
        if (confirmState.kind === "toggle_sandbox")
            return confirmToggleFlag("EMAIL_ENGINE_SEND_SANDBOX", confirmState.next);
        if (confirmState.kind === "dispatch")
            return confirmDispatch(confirmState.suggestion);
        if (confirmState.kind === "schedule")
            return confirmSchedule(confirmState.suggestion);
        if (confirmState.kind === "cancel")
            return confirmCancel(confirmState.log);
        if (confirmState.kind === "dispatch_due")
            return confirmDispatchDue(confirmState.log);
        if (confirmState.kind === "retract")
            return confirmRetract(confirmState.log);
        if (confirmState.kind === "rollout_advance"
            || confirmState.kind === "rollout_rollback")
            return confirmRollout();
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main
                    className="flex-1 p-6 md:p-8 max-w-6xl mx-auto w-full"
                    data-testid="email-send-dashboard"
                >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-[var(--color-secondary)] mb-1">
                                Phase 15 · Email Send Layer
                            </div>
                            <h1 className="text-3xl md:text-4xl font-semibold text-text-main flex items-center gap-3">
                                <Send className="h-7 w-7 text-[var(--color-primary)]" />
                                Dispatch acknowledged emails
                            </h1>
                            <p className="text-sm text-text-secondary mt-1 max-w-2xl">
                                Second phase with side-effects. Sandbox-first: dispatch logs to <code>email_send_logs</code> with
                                zero external calls. Every send includes a unique unsubscribe token — GDPR/CAN-SPAM compliant.
                                No 13th autonomy type: this is a downstream action on the 11th queue (email suggestions).
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <a
                                href="/admin/email-engine"
                                data-testid="email-send-back-engine"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-1"
                            >
                                Email Engine <ArrowUpRight className="h-3 w-3" />
                            </a>
                            <button
                                onClick={loadFlagsAndTotals}
                                data-testid="email-send-refresh"
                                className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-2"
                            >
                                <RefreshCw className="h-3 w-3" /> Refresh
                            </button>
                        </div>
                    </div>

                    <FlagBanner
                        statusData={statusData}
                        isAdmin={isAdmin}
                        onToggleMaster={askToggleMaster}
                        onToggleSandbox={askToggleSandbox}
                        busy={flagBusy}
                    />

                    <RolloutBanner
                        rollout={rolloutData}
                        isAdmin={isAdmin}
                        onAdvance={askAdvanceRollout}
                        onRollback={askRollbackRollout}
                        busy={flagBusy}
                    />

                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                        <KpiCard
                            icon={Sparkles}
                            label="Acknowledged applicable"
                            value={totals.acknowledged_email_suggestions || 0}
                            tone="secondary"
                            testid="email-send-kpi-acknowledged"
                            hint="Ready to dispatch or schedule."
                        />
                        <KpiCard
                            icon={Send}
                            label="Dispatched last 7d"
                            value={totals.dispatched_last_7d || 0}
                            tone="primary"
                            testid="email-send-kpi-dispatched"
                        />
                        <KpiCard
                            icon={Calendar}
                            label="Scheduled pending"
                            value={totals.scheduled_pending || 0}
                            tone="success"
                            testid="email-send-kpi-scheduled"
                            hint="Awaiting scheduled_at."
                        />
                        <KpiCard
                            icon={Timer}
                            label="Due now"
                            value={totals.scheduled_due_now || 0}
                            tone="warning"
                            testid="email-send-kpi-due"
                            hint="Manual Send now required."
                        />
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-16">
                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                        </div>
                    ) : !statusData ? (
                        <div
                            data-testid="email-send-disabled-state"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-10 text-center"
                        >
                            <ShieldAlert className="h-8 w-8 text-[var(--color-alert)] mx-auto mb-3" />
                            <p className="text-sm text-text-secondary max-w-lg mx-auto">
                                Enable <span className="text-text-main font-mono">EMAIL_ENGINE_SEND_ENABLED</span> to load the
                                applicable queue and the dispatch log. Sandbox mode (default ON) prevents any real delivery.
                            </p>
                        </div>
                    ) : (
                        <div className="grid gap-6 lg:grid-cols-2">
                            {/* Applicable queue */}
                            <section
                                data-testid="email-send-queue"
                                className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden flex flex-col"
                            >
                                <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                                    <div>
                                        <h2 className="text-base font-semibold text-text-main">
                                            Applicable queue
                                        </h2>
                                        <p className="text-xs text-text-secondary">
                                            Acknowledged from generator / upgrade / credits / retention.
                                        </p>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-widest text-text-secondary tabular-nums">
                                        {suggestions.length} ready
                                    </span>
                                </div>
                                <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
                                    {suggestions.length === 0 ? (
                                        <div className="text-center py-10">
                                            <CheckCircle2 className="h-6 w-6 text-[var(--color-success)] mx-auto mb-2" />
                                            <p className="text-xs text-text-secondary">
                                                Nothing to dispatch. Acknowledge suggestions in{" "}
                                                <a href="/admin/email-engine" className="text-[var(--color-primary)] underline">
                                                    /admin/email-engine
                                                </a>.
                                            </p>
                                        </div>
                                    ) : (
                                        <ul className="space-y-2">
                                            {suggestions.map((s) => (
                                                <SuggestionRow
                                                    key={s.id}
                                                    suggestion={s}
                                                    onDispatch={askDispatch}
                                                    onSchedule={askSchedule}
                                                    onPreview={doPreview}
                                                    disabled={busy || previewBusy}
                                                />
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </section>

                            {/* Scheduled + History */}
                            <section
                                data-testid="email-send-log-section"
                                className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden flex flex-col"
                            >
                                <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-center justify-between">
                                    <div>
                                        <h2 className="text-base font-semibold text-text-main">
                                            Scheduled + History
                                        </h2>
                                        <p className="text-xs text-text-secondary">
                                            Immutable audit — last 50.
                                        </p>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-widest text-text-secondary tabular-nums">
                                        {logs.length} entries
                                    </span>
                                </div>
                                <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
                                    {scheduledOnly.length > 0 && (
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-2 flex items-center gap-1">
                                                <Timer className="h-3 w-3" /> Scheduled / due ({scheduledOnly.length})
                                            </div>
                                            <ul className="space-y-2">
                                                {scheduledOnly.map((l) => (
                                                    <LogRow
                                                        key={l.id}
                                                        log={l}
                                                        onCancel={askCancel}
                                                        onDispatchDue={askDispatchDue}
                                                        onRetract={askRetract}
                                                        disabled={busy}
                                                    />
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-2 flex items-center gap-1">
                                            <History className="h-3 w-3" /> History ({historyOnly.length})
                                        </div>
                                        {historyOnly.length === 0 ? (
                                            <p className="text-xs text-text-secondary text-center py-6">
                                                No dispatch yet.
                                            </p>
                                        ) : (
                                            <ul className="space-y-2">
                                                {historyOnly.map((l) => (
                                                    <LogRow
                                                        key={l.id}
                                                        log={l}
                                                        onCancel={askCancel}
                                                        onDispatchDue={askDispatchDue}
                                                        onRetract={askRetract}
                                                        disabled={busy}
                                                    />
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </section>
                        </div>
                    )}
                </main>
            </div>

            {/* Confirm modal */}
            <ConfirmModal
                open={!!confirmState}
                title={confirmState?.title || ""}
                subtitle={confirmState?.subtitle}
                tone={confirmState?.tone}
                confirmLabel={confirmState?.confirmLabel || "Confirm"}
                onConfirm={confirmHandler}
                onCancel={() => setConfirmState(null)}
                busy={busy || flagBusy}
            >
                {confirmState?.kind === "schedule" ? (
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-text-secondary block mb-1">
                                Scheduled at (local)
                            </label>
                            <input
                                type="datetime-local"
                                data-testid="email-send-schedule-at"
                                value={scheduleAt}
                                onChange={(e) => setScheduleAt(e.target.value)}
                                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] text-text-main px-3 py-2 text-sm"
                            />
                            <p className="text-[10px] text-text-secondary mt-1">
                                Must be 5+ minutes and ≤ 30 days from now.
                            </p>
                        </div>
                        <div className="rounded-lg border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-3 text-[11px] text-text-secondary">
                            Recipients are frozen at schedule-time. When the cron ticks, the entry moves to <code>due</code> — you review + click Send now.
                        </div>
                    </div>
                ) : confirmState?.kind === "retract" ? (
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-text-secondary block mb-1">
                                Reason (required)
                            </label>
                            <textarea
                                data-testid="email-send-retract-reason"
                                value={retractReason}
                                onChange={(e) => setRetractReason(e.target.value)}
                                rows={3}
                                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] text-text-main px-3 py-2 text-sm"
                                placeholder="e.g., Sent to wrong segment, compliance follow-up requested…"
                            />
                        </div>
                        <div className="rounded-lg border border-[var(--color-alert)]/30 bg-[var(--color-alert)]/5 p-3 text-[11px] text-text-secondary">
                            <span className="text-[var(--color-alert)] font-semibold">Audit label only.</span>{" "}
                            The email has already left the queue. Retract does NOT recall it, does NOT re-send, does NOT delete the log.
                        </div>
                    </div>
                ) : (confirmState?.kind === "rollout_advance"
                        || confirmState?.kind === "rollout_rollback") ? (
                    <div className="space-y-3">
                        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3">
                            <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                                Transition
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <span className="font-mono text-text-secondary">
                                    Step {confirmState.fromStep}
                                </span>
                                <span className="text-[var(--color-primary)]">→</span>
                                <span className="font-mono text-text-main font-semibold">
                                    Step {confirmState.toStep}
                                </span>
                                <span className="text-[11px] text-text-secondary ml-2">
                                    {(STEP_INFO[confirmState.toStep] || {}).name}
                                </span>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-text-secondary block mb-1">
                                Reason (required, audited)
                            </label>
                            <textarea
                                data-testid="rollout-confirm-reason"
                                value={rolloutReason}
                                onChange={(e) => setRolloutReason(e.target.value)}
                                rows={2}
                                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] text-text-main px-3 py-2 text-sm"
                                placeholder="e.g., Smoke-test complete on admin cohort — opening tiny segment"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-text-secondary block mb-1">
                                Type <span className="text-text-main font-mono">
                                    {confirmState.kind === "rollout_advance" ? "ADVANCE" : "ROLLBACK"}
                                </span> to confirm
                            </label>
                            <input
                                type="text"
                                data-testid="rollout-confirm-type"
                                value={rolloutConfirmType}
                                onChange={(e) => setRolloutConfirmType(e.target.value)}
                                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-ai)] text-text-main px-3 py-2 text-sm font-mono uppercase tracking-widest"
                                placeholder={confirmState.kind === "rollout_advance" ? "ADVANCE" : "ROLLBACK"}
                            />
                        </div>
                    </div>
                ) : (
                    confirmState?.children
                )}
            </ConfirmModal>

            {/* Preview modal */}
            <AnimatePresence>
                {previewData && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
                            onClick={() => setPreviewData(null)}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 20 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        >
                            <div
                                data-testid="email-send-preview-modal"
                                className="pointer-events-auto w-full max-w-3xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                            >
                                <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                    <div>
                                        <h3 className="text-base font-semibold text-text-main">
                                            Rendered preview
                                        </h3>
                                        <p className="text-[11px] text-text-secondary mt-1">
                                            Rendered for user <span className="font-mono">{me?.email}</span> · unsubscribe URL is a preview token
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setPreviewData(null)}
                                        data-testid="email-send-preview-close"
                                        className="text-text-secondary hover:text-text-main"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="p-5 overflow-y-auto flex-1 space-y-3">
                                    {previewData.loading ? (
                                        <div className="flex justify-center py-10">
                                            <Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3">
                                                <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">Subject</div>
                                                <div className="text-sm text-text-main">{previewData.subject}</div>
                                            </div>
                                            {previewData.missing_placeholders?.length > 0 && (
                                                <div className="rounded-lg border border-neutral-700 bg-neutral-900 p-3 text-[11px] text-neutral-300 flex items-start gap-2">
                                                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                                                    <div>
                                                        Missing placeholders: {previewData.missing_placeholders.join(", ")}
                                                    </div>
                                                </div>
                                            )}
                                            <div
                                                data-testid="email-send-preview-html"
                                                className="rounded-xl border border-[var(--color-border)] bg-white overflow-hidden"
                                            >
                                                <iframe
                                                    title="preview"
                                                    srcDoc={previewData.body_html || ""}
                                                    className="w-full h-96 bg-white"
                                                    sandbox=""
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
